import { css } from "lit";
import { baseStyles } from "../../theme/theme";

export const scheduleStyles = [
  baseStyles,
  css`
  .offering-badge { display: inline-block; padding: 2px 6px; margin-left: 4px; border-radius: 4px; background: var(--z-subtle, #f3f4f6); color: var(--z-text, #374151); font-size: 11px; font-weight: 600; white-space: nowrap; }
  button:disabled { cursor: not-allowed; opacity: .55; }

    .wrap {
      max-width: 960px;
      margin: 0 auto;
      padding: 1.25rem 1.25rem 2.5rem;
    }
    /* Header */
    .head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      flex-wrap: wrap;
      margin-bottom: 1.25rem;
    }
    .title {
      font-size: 2.125rem;
      font-weight: var(--zui-weight, 500);
      letter-spacing: -0.02em;
      color: var(--zui-headline);
      margin: 0;
    }
    /* 7/25 QA: the sign-in/out control is the shared '.zui-btn-secondary'
         outline button from 'theme/theme.ts' — the same treatment the events
         register flow and both checkouts now use — so "Sign in" reads as a
         button everywhere instead of accent-colored text here and a button
         there. '.signin' is kept purely as a stable hook for the AUTH-040…042
         presentation lock; it deliberately declares nothing, because any
         property it set would win over the shared class (component styles are
         listed after 'baseStyles') and silently fork the treatment again. */
    .controls {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      flex-wrap: wrap;
      margin-bottom: 1rem;
      padding: 0;
      background: transparent;
      border: 0;
      border-radius: 0;
    }
    .filters {
      display: flex;
      gap: 0.5rem;
      flex-wrap: wrap;
    }
    /* Filter pills */
    .pill {
      position: relative;
      display: inline-flex;
    }
    .pill select {
      appearance: none;
      font: inherit;
      font-size: 13px;
      font-weight: 600;
      padding: 0.5rem 2rem 0.5rem 0.875rem;
      border: 1px solid var(--zui-line);
      /* X2: "square" flattens the filter controls too; rounded/pill/unset
           keep the pill shape via the var() fallback. */
      border-radius: var(--zui-square-radius, 999px);
      background: #fff;
      color: var(--zui-panel-ink);
      cursor: pointer;
    }
    .pill.active select {
      background: var(--zui-accent-soft);
      border-color: var(--zui-accent);
      color: var(--zui-accent-deep);
    }
    .pill .chev {
      position: absolute;
      right: 0.7rem;
      top: 50%;
      transform: translateY(-50%);
      pointer-events: none;
      color: var(--zui-panel-ink-soft);
      display: inline-flex;
    }
    .switch-wrap {
      position: relative;
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      flex: 0 0 auto;
    }
    .switcher {
      display: inline-flex;
      border: 1px solid var(--zui-line);
      border-radius: var(--zui-btn-radius, 999px);
      overflow: hidden;
    }
    .switcher button {
      appearance: none;
      border: 0;
      background: #fff;
      color: var(--zui-panel-ink-soft);
      font: inherit;
      font-size: 0.8rem;
      /* SCH-02: trimmed vertical padding so the control reads balanced
           against its horizontal padding instead of a tall pill. */
      padding: 0.3rem 0.9rem;
      cursor: pointer;
    }
    .switcher button[aria-pressed="true"] {
      background: var(--zui-accent);
      color: var(--zui-btn-text);
    }
    /* N2: timezone picker — a native <select> of U.S. time zones, restyled
         from the old segmented [studio zone | your zone] toggle. Styled to
         match the filter pills (same --zui tokens + square-radius, leading
         clock glyph, trailing chevron) so it reads as one of the controls and
         "square" flattens it too. Sits alongside the filter pills; only
         rendered when there's schedule data with a resolvable zone (see
         canSwitchTz). */
    .tz-pill {
      position: relative;
      display: inline-flex;
      align-items: center;
      flex: 0 0 auto;
      gap: 0.35rem;
      padding-left: 0.75rem;
      border: 1px solid var(--zui-line);
      border-radius: var(--zui-square-radius, 999px);
      background: #fff;
      color: var(--zui-panel-ink);
    }
    .tz-pill .tz-ico {
      display: inline-flex;
      color: var(--zui-panel-ink-soft);
      opacity: 0.85;
      pointer-events: none;
    }
    .tz-pill select {
      appearance: none;
      font: inherit;
      font-size: 13px;
      font-weight: 600;
      border: 0;
      margin: 0;
      /* Right padding clears the absolutely-positioned chevron; the leading
           clock glyph provides the left inset via the wrapper's padding. */
      padding: 0.5rem 1.9rem 0.5rem 0.15rem;
      background: transparent;
      color: var(--zui-panel-ink);
      border-radius: inherit;
      cursor: pointer;
    }
    .tz-pill select:focus-visible {
      outline: none;
    }
    .tz-pill .chev {
      position: absolute;
      right: 0.7rem;
      top: 50%;
      transform: translateY(-50%);
      pointer-events: none;
      color: var(--zui-panel-ink-soft);
      display: inline-flex;
    }
    /* Mobile-only affordances (hidden on desktop, revealed in the
         max-width:480px block): a single "Filter" trigger that opens the
         filter sheet, and a view dropdown replacing the text switcher. */
    .filter-trigger {
      display: none;
      appearance: none;
      align-items: center;
      gap: 0.4rem;
      border: 1px solid var(--zui-line);
      border-radius: var(--zui-btn-radius, 999px);
      background: #fff;
      color: var(--zui-panel-ink);
      font: inherit;
      font-size: 0.85rem;
      font-weight: 600;
      padding: 0.4rem 0.8rem;
      cursor: pointer;
      position: relative;
      white-space: nowrap;
    }
    .filter-trigger.active {
      background: var(--zui-accent-soft);
      border-color: var(--zui-accent);
      color: var(--zui-accent-deep);
    }
    .view-select {
      display: none;
      appearance: none;
      padding: 0.4rem 2rem 0.4rem 0.8rem;
      border: 1px solid var(--zui-line);
      border-radius: var(--zui-btn-radius, 999px);
      background: #fff;
      color: var(--zui-panel-ink);
      font: inherit;
      font-size: 0.85rem;
      font-weight: 600;
      line-height: 1.6;
      cursor: pointer;
    }
    /* Mobile filter sheet (opened by .filter-trigger). */
    .fs-overlay {
      position: fixed;
      inset: 0;
      z-index: 2147483000;
      background: rgba(20, 24, 28, 0.45);
      display: flex;
      align-items: flex-end;
      justify-content: center;
      /* SC6: promoted to the top layer via popover="manual" — neutralize the
           UA [popover] defaults so it fills the viewport as a backdrop. */
      width: auto;
      height: auto;
      max-width: none;
      max-height: none;
      margin: 0;
      border: 0;
      overflow: visible;
    }
    .fs-sheet {
      background: #fff;
      color: var(--zui-headline);
      width: 100%;
      max-width: 520px;
      max-height: 80vh;
      display: flex;
      flex-direction: column;
      border-radius: 18px 18px 0 0;
      box-shadow: 0 -12px 40px -12px rgba(0, 0, 0, 0.35);
    }
    .fs-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 1rem 1.125rem 0.75rem;
      font-family: var(--zui-font);
      font-weight: 700;
      font-size: 1rem;
      border-bottom: 1px solid var(--zui-line);
    }
    .fs-close {
      appearance: none;
      background: none;
      border: 0;
      font: inherit;
      font-size: 1.2rem;
      line-height: 1;
      color: var(--zui-panel-ink-soft);
      cursor: pointer;
      padding: 0 0.25rem;
    }
    .fs-body {
      overflow-y: auto;
      padding: 0.875rem 1.125rem;
      display: flex;
      flex-direction: column;
      gap: 0.875rem;
    }
    .fs-row {
      display: flex;
      flex-direction: column;
      gap: 0.4rem;
    }
    .fs-label {
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: var(--zui-panel-ink-soft);
    }
    .fs-foot {
      display: flex;
      gap: 0.75rem;
      padding: 0.875rem 1.125rem;
      border-top: 1px solid var(--zui-line);
    }
    .fs-clear {
      appearance: none;
      flex: 1;
      border: 1px solid var(--zui-line);
      border-radius: var(--zui-btn-radius, 999px);
      background: #fff;
      color: var(--zui-panel-ink);
      font: inherit;
      font-weight: 600;
      font-size: 0.9rem;
      padding: 0.6rem 1rem;
      cursor: pointer;
    }
    .fs-clear:disabled {
      opacity: 0.5;
      cursor: default;
    }
    .fs-done {
      appearance: none;
      flex: 1;
      border: 0;
      border-radius: var(--zui-btn-radius, 999px);
      background: var(--zui-accent);
      color: var(--zui-btn-text);
      font: inherit;
      font-weight: 700;
      font-size: 0.9rem;
      padding: 0.6rem 1rem;
      cursor: pointer;
    }
    /* Last-resort scroll target (scrollToDay falls back to the host when no
         day section is rendered at all) — clear the host page's own chrome. */
    :host {
      scroll-margin-top: calc(var(--zui-sticky-offset, 0px) + 8px);
    }
    /* Sticky header block: the filter row + the day navigator travel
         together. The filters used to scroll away while only the strip stuck,
         which left the visitor with a date navigator they couldn't filter. */
    .sticky-head {
      position: sticky;
      /* Host pages with their own sticky header can set
           '--zui-sticky-offset' (e.g. style="--zui-sticky-offset: 64px" on
           the <zipper-schedule> element, or any ancestor) to that header's
           height so this block docks just below it instead of clipping
           under/overlapping it. Defaults to 0 (flush with viewport top). */
      top: var(--zui-sticky-offset, 0px);
      z-index: 30;
      /* Opaque so list rows scroll cleanly UNDER the header. N3: the
           bottom drop-shadow was removed — it read as a stray line/border
           under the week-preview strip; the opaque background alone keeps
           rows from showing through while scrolling. '--zui-sticky-bg' is
           the theme background forced opaque — NOT '--zui-bg', which host
           pages may override to transparent (see themeToCssVars). */
      background: var(--zui-sticky-bg, #fff);
      padding-top: 0.5rem;
    }
    /* The controls row sits inside the sticky block now, so its old
         standalone bottom margin would pad the sticky chrome out needlessly. */
    .sticky-head .controls {
      margin-bottom: 0.625rem;
    }
    .strip-wrap {
      background: var(--zui-sticky-bg, #fff);
      padding: 0 0 0.625rem;
    }
    .strip-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      /* 7/25 QA: the sign-in control in the actions group is now a bordered
           secondary button rather than bare text, so this row needs more room
           than it used to. Wrap intrinsically (no viewport breakpoint — the
           embed is sized by its host container, not the viewport) so a narrow
           host stacks the actions under the month label instead of overflowing
           or squashing them. */
      flex-wrap: wrap;
      gap: 0.5rem 0.75rem;
      margin-bottom: 0.5rem;
      padding: 0 2px;
    }
    .strip-bar-actions {
      display: inline-flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 0.6rem;
      flex: 0 1 auto;
      justify-content: flex-end;
      margin-left: auto;
    }
    .month-label {
      appearance: none;
      background: transparent;
      border: 0;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
      font: inherit;
      font-size: 15px;
      font-weight: 700;
      letter-spacing: -0.005em;
      color: var(--zui-headline);
    }
    .strip {
      position: relative;
      display: flex;
      align-items: center;
      gap: 4px;
      background: var(--zui-panel);
      border: 1px solid var(--zui-line);
      border-radius: var(--zui-square-radius, var(--zui-btn-radius, 14px));
      padding: 0.875rem 0.5rem;
    }
    .arrow {
      appearance: none;
      flex: 0 0 auto;
      width: 32px;
      height: 32px;
      border-radius: 999px;
      border: 1px solid var(--zui-line);
      background: #fff;
      color: var(--zui-panel-ink);
      display: grid;
      place-items: center;
      cursor: pointer;
    }
    .days {
      flex: 1;
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 4px;
    }
    .day {
      appearance: none;
      background: transparent;
      border: 0;
      padding: 0;
      cursor: pointer;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
    }
    .day .cell {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2px;
      padding: 7px 10px;
      border-radius: var(--zui-square-radius, 12px);
      min-width: 44px;
      transition: background 0.15s ease;
    }
    .day[aria-selected="true"] .cell {
      background: var(--zui-accent);
    }
    .day .dow {
      font-size: 10.5px;
      font-weight: 700;
      letter-spacing: 0.1em;
      text-transform: uppercase;
      color: var(--zui-panel-ink-soft);
    }
    .day .dow-short {
      display: none;
    }
    .day[aria-selected="true"] .dow {
      color: rgba(255, 255, 255, 0.9);
    }
    .day .num {
      font-weight: 700;
      font-size: 16px;
      color: var(--zui-panel-ink);
    }
    .day[aria-selected="true"] .num {
      color: #fff;
    }
    .day .dot {
      width: 4px;
      height: 4px;
      border-radius: 999px;
      background: transparent;
    }
    .day .dot.on {
      background: var(--zui-line);
    }
    .day .dot.today {
      background: var(--zui-accent);
    }
    .today-pill {
      appearance: none;
      flex: 0 0 auto;
      background: #fff;
      border: 1px solid var(--zui-line);
      border-radius: var(--zui-btn-radius, 999px);
      padding: 4px 12px;
      font: inherit;
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--zui-accent);
      cursor: pointer;
      white-space: nowrap;
    }
    /* Month/year picker modal */
    .mp-overlay {
      position: fixed;
      inset: 0;
      z-index: 2147483000;
      background: rgba(20, 24, 28, 0.45);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 1rem;
      /* SC6: promoted to the top layer via popover="manual" — neutralize the
           UA [popover] defaults so it fills the viewport as a backdrop. */
      width: auto;
      height: auto;
      max-width: none;
      max-height: none;
      margin: 0;
      border: 0;
      overflow: visible;
    }
    .mp-sheet {
      background: #fff;
      border-radius: 16px;
      width: 100%;
      max-width: 340px;
      padding: 1rem;
      box-shadow: 0 24px 60px -20px rgba(0, 0, 0, 0.45);
    }
    .mp-head {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 0.75rem;
    }
    .mp-year {
      font-family: var(--zui-font);
      font-weight: 700;
      font-size: 1.05rem;
      color: var(--zui-headline);
    }
    .mp-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 0.5rem;
    }
    .mp-month {
      appearance: none;
      border: 1px solid var(--zui-line);
      border-radius: 10px;
      background: #fff;
      color: var(--zui-headline);
      font: inherit;
      font-size: 0.9rem;
      font-weight: 600;
      padding: 0.6rem 0.25rem;
      cursor: pointer;
    }
    .mp-month:hover {
      background: var(--zui-panel);
    }
    .mp-month.sel {
      background: var(--zui-accent);
      border-color: var(--zui-accent);
      color: var(--zui-btn-text);
    }
    .mp-back {
      appearance: none;
      border: 0;
      background: transparent;
      color: var(--zui-accent);
      font: inherit;
      font-size: 0.85rem;
      font-weight: 600;
      cursor: pointer;
      padding: 0.25rem 0;
    }
    .mp-day-grid {
      display: grid;
      grid-template-columns: repeat(7, 1fr);
      gap: 3px;
    }
    .mp-dow {
      text-align: center;
      font-size: 0.65rem;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: var(--zui-panel-ink-soft);
      padding: 0.2rem 0;
    }
    .mp-day {
      appearance: none;
      aspect-ratio: 1;
      border: 1px solid var(--zui-line);
      border-radius: 8px;
      background: #fff;
      color: var(--zui-headline);
      font: inherit;
      font-size: 0.8rem;
      font-weight: 600;
      cursor: pointer;
      display: grid;
      place-items: center;
      padding: 0;
    }
    .mp-day:hover:not(:disabled) {
      background: var(--zui-panel);
    }
    .mp-day.today {
      background: var(--zui-accent-soft);
      border-color: var(--zui-accent);
      color: var(--zui-accent-deep);
    }
    .mp-day.sel {
      background: var(--zui-accent);
      border-color: var(--zui-accent);
      color: var(--zui-btn-text);
    }
    .mp-day:disabled {
      opacity: 0.35;
      cursor: default;
    }
    .mp-day.empty {
      border: 0;
      background: transparent;
      cursor: default;
    }
    /* Day sections */
    .single-day-state {
      text-align: center;
    }
    .days-list {
      margin-top: 1rem;
      display: flex;
      flex-direction: column;
      gap: 1.75rem;
    }
    .day-head {
      font-family: var(--zui-font);
      font-weight: 700;
      font-size: 20px;
      letter-spacing: -0.01em;
      color: var(--zui-headline);
      margin: 0 0 0.25rem;
    }
    section[data-offset] {
      /* Clearance for this component's own sticky header, plus whatever
           additional offset the host page's sticky header needs (see
           --zui-sticky-offset on .sticky-head above), plus a little breathing
           room so the day heading isn't flush against the chrome.

           '--zui-sticky-h' is the sticky block's MEASURED height, written by
           syncStickyOffset() before every programmatic scroll — the block
           grows when filter pills wrap or the Today pill appears, so the old
           hardcoded 96px landed the heading under the chrome at some widths.
           The 96px literal survives only as the pre-measurement fallback. */
      scroll-margin-top: calc(
        var(--zui-sticky-h, 96px) + var(--zui-sticky-offset, 0px) + 8px
      );
    }
    .row[data-occurrence-id] {
      /* Initial list positioning targets a class, not merely its day. Reuse
         the same live measurement as day navigation so both desktop and
         wrapped mobile chrome stay clear of the destination. */
      scroll-margin-top: calc(
        var(--zui-sticky-h, 96px) + var(--zui-sticky-offset, 0px) + 8px
      );
    }
    /* Class row */
    .row {
      display: grid;
      grid-template-columns: 108px 48px 1fr auto;
      gap: 1rem;
      align-items: center;
    }
    .row.lines {
      border-bottom: 1px solid var(--zui-line);
      padding: 1rem 2px;
    }
    .row.cards {
      background: #fff;
      border: 1px solid var(--zui-line);
      border-radius: var(--zui-square-radius, 14px);
      padding: 1rem 1.25rem;
      margin-bottom: 0.5rem;
    }
    .row.no-thumb {
      grid-template-columns: 108px 1fr auto;
    }
    .time {
      font-family: var(--zui-font);
      font-weight: 700;
      font-size: 20px;
      letter-spacing: -0.005em;
      color: var(--zui-headline);
    }
    .time.cancelled {
      text-decoration: line-through;
      color: var(--zui-panel-ink-soft);
    }
    .dur {
      font-size: 12.5px;
      color: var(--zui-panel-ink-soft);
      margin-top: 2px;
    }
    .thumb {
      width: 48px;
      height: 48px;
      border-radius: 999px;
      overflow: hidden;
      background: var(--zui-accent-soft);
      display: grid;
      place-items: center;
      color: var(--zui-accent-deep);
      font-weight: 700;
      font-size: 14px;
    }
    .thumb img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
    }
    .cname {
      display: inline-flex;
      align-items: flex-start;
      gap: 0.4rem;
      font-family: var(--zui-font);
      font-weight: 700;
      font-size: 17px;
      letter-spacing: -0.005em;
      line-height: 1.3;
      color: var(--zui-headline);
    }
    .cname.cancelled {
      text-decoration: line-through;
      color: var(--zui-panel-ink-soft);
    }
    .cname.expandable {
      cursor: pointer;
      display: inline-flex;
      align-items: flex-start;
      gap: 0.35rem;
      flex-wrap: wrap;
    }
    .cname .more-details {
      font-size: 11px;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: var(--zui-accent);
      padding: 0.15rem 0.35rem;
      border-radius: 6px;
      line-height: 1.2;
    }
    .cname .more-details:hover {
      background: var(--zui-accent-soft);
    }
    .cname .exp-icon {
      display: inline-flex;
      flex-shrink: 0;
      margin-top: 0.25em;
      transition: transform 0.15s ease;
    }
    .cname.expanded .exp-icon {
      transform: rotate(180deg);
    }
    .cdesc {
      grid-column: 1 / -1;
      font-size: 13px;
      line-height: 1.5;
      color: var(--zui-panel-ink-soft);
      margin-top: 0.5rem;
      padding-right: 0.5rem;
      white-space: pre-line;
      overflow-wrap: anywhere;
    }
    .cmeta {
      font-size: 13px;
      color: var(--zui-panel-ink-soft);
      margin-top: 2px;
      line-height: 1.5;
    }
    /* SCH-09: location links out to Google Maps in a new tab — reads as a
         plain accent-colored link, with a solid underline appearing only on
         hover/focus (no dashed border at rest, so it stays clean when the
         .cmeta children are stacked full-width on mobile). */
    .cmeta a.loc {
      color: var(--zui-accent);
      text-decoration: none;
      cursor: pointer;
    }
    .cmeta a.loc:hover,
    .cmeta a.loc:focus-visible {
      color: var(--zui-accent-deep);
      text-decoration: underline;
      text-underline-offset: 2px;
    }
    /* SCH-08: instructor name becomes an inline accordion toggle when the
         class carries an instructor bio — the caret alone is the affordance
         (no underline on the name), caret rotates on expand. */
    .cmeta .ibio-btn.expandable {
      cursor: pointer;
    }
    .cmeta .ibio-btn .ibio-name {
      display: inline;
    }
    .cmeta .ibio-btn .exp-icon {
      display: inline-flex;
      vertical-align: middle;
      margin-left: 0.15rem;
      color: var(--zui-accent);
      transition: transform 0.15s ease;
    }
    .cmeta .ibio-btn.expanded .exp-icon {
      transform: rotate(180deg);
    }
    /* SCH-08: expanded instructor-bio panel — spans the full row like the
         description panel (.cdesc) and renders sanitized WYSIWYG bio HTML. */
    .cbio {
      grid-column: 1 / -1;
      font-size: 13px;
      line-height: 1.5;
      color: var(--zui-panel-ink-soft);
      margin-top: 0.5rem;
      padding-right: 0.5rem;
    }
    .cbio :first-child {
      margin-top: 0;
    }
    .cbio :last-child {
      margin-bottom: 0;
    }
    .cap {
      font-size: 12.5px;
      font-weight: 600;
      color: var(--zui-headline);
      white-space: nowrap;
    }
    .cap.waiting {
      font-weight: 500;
      color: var(--zui-panel-ink-soft);
    }
    .cancelled-note {
      font-size: 12.5px;
      font-weight: 700;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: var(--zui-headline);
      white-space: nowrap;
      text-align: center;
    }
    .right {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }
    /* Cancelled rows have only the "CANCELLED" note in the right column —
         keep it vertically centered against the (often taller, wrapped) title
         instead of top-pinned. */
    .right.is-cancelled {
      align-self: center;
      justify-content: center;
    }
    a.book,
    a.wk-book,
    a.cal-pop-book {
      display: inline-block;
      box-sizing: border-box;
      text-align: center;
      text-decoration: none;
    }
    a.book {
      margin-inline-start: auto;
    }
    .book {
      appearance: none;
      cursor: pointer;
      font-family: var(--zui-font);
      font-weight: 700;
      font-size: 13.5px;
      letter-spacing: -0.005em;
      border-radius: var(--zui-btn-radius, 10px);
      padding: 0.625rem 1.125rem;
      white-space: nowrap;
      border: 0;
      background: var(--zui-accent);
      color: var(--zui-btn-text);
    }
    .book.waitlist {
      background: #fff;
      color: var(--zui-accent);
      border: 1.5px solid var(--zui-accent);
    }
    .book.buyout {
      background: #fff;
      color: var(--zui-accent);
      border: 1.5px solid var(--zui-accent);
    }
    /* SCH-11: full label reads normally at this width; the short
         one-word variant only takes over on narrow phones (see mobile
         breakpoint) where "Book class" no longer fits on one line. */
    .book-label-short {
      display: none;
    }
    /* Calendar (month) view.
         · desktop = full unified month grid (shared borders, prev/next-month
           overflow days greyed, today's number circled). Class bars; clicking a
           bar opens a detail popover with the row info + Book (like list/mobile).
         · mobile  = compact grid with dot indicators + a day-detail list below
           (tap a day → its classes with Book buttons). */
    .cal {
      padding: 0.25rem 0 1rem;
      position: relative;
    }
    .cal-toolbar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      margin-bottom: 0.75rem;
    }
    .cal-tb-nav {
      display: inline-flex;
      align-items: center;
      gap: 0.4rem;
    }
    .cal-nav-btn {
      appearance: none;
      width: 1.9rem;
      height: 1.9rem;
      display: grid;
      place-items: center;
      border: 1px solid var(--zui-line);
      /* N4: Square shape flattens the nav arrows too (0px); rounded/pill/unset
           keep the 8px corner via the var() fallback. */
      border-radius: var(--zui-square-radius, 8px);
      background: #fff;
      color: var(--zui-headline);
      cursor: pointer;
    }
    .cal-nav-btn:hover {
      background: var(--zui-panel);
    }
    .cal-today-btn {
      appearance: none;
      border: 1px solid var(--zui-line);
      /* N4: Square shape flattens the Today button (0px); other shapes keep 8px. */
      border-radius: var(--zui-square-radius, 8px);
      background: #fff;
      color: var(--zui-headline);
      font: inherit;
      font-size: 0.8rem;
      font-weight: 600;
      padding: 0.35rem 0.85rem;
      cursor: pointer;
    }
    .cal-today-btn:hover {
      background: var(--zui-panel);
    }
    .cal-month-btn {
      appearance: none;
      border: 0;
      background: none;
      cursor: pointer;
      font-family: var(--zui-font);
      font-weight: 700;
      font-size: 1.05rem;
      color: var(--zui-headline);
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
    }
    .cal-tb-spacer {
      width: 5.6rem;
    }
    .cal-grid {
      border: 1px solid var(--zui-line);
      border-radius: var(--zui-square-radius, 8px);
      overflow: hidden;
    }
    .cal-dow-row {
      display: grid;
      /* A3: minmax(0, 1fr) — same fix as .wk-grid below — pins every track's
           minimum to 0 instead of the default 'auto' (content-based) minimum.
           Without it, a day whose class bars carry an unbreakable long title
           inflates that column's automatic minimum size past 1/7 of the row,
           and the whole grid (clipped by .cal-grid's overflow: hidden) blows
           out to the right — reproducible on any month with a long class
           name/instructor combo, not just "the 2nd month you view". */
      grid-template-columns: repeat(7, minmax(0, 1fr));
      background: var(--zui-panel);
      border-bottom: 1px solid var(--zui-line);
    }
    .cal-cells {
      display: grid;
      grid-template-columns: repeat(7, minmax(0, 1fr));
    }
    .cal-dow {
      font-size: 0.64rem;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: var(--zui-details);
      text-align: center;
      padding: 0.45rem 0;
      background: var(--zui-panel);
      border-right: 1px solid var(--zui-line);
      border-bottom: 1px solid var(--zui-line);
    }
    .cal-dow:nth-child(7) {
      border-right: 0;
    }
    .day.past,
    .cal-cell.past {
      opacity: 0.55;
    }
    .row.past {
      opacity: 0.62;
    }
    .sub-badge {
      display: inline-block;
      margin-left: 0.35rem;
      padding: 0.1rem 0.35rem;
      border-radius: 4px;
      font-size: 0.62rem;
      font-weight: 700;
      letter-spacing: 0.03em;
      text-transform: uppercase;
      background: var(--zui-accent-soft);
      color: var(--zui-accent-deep);
      vertical-align: middle;
    }
    .cal-cell {
      min-height: 92px;
      /* A3: let the cell shrink below its content's intrinsic width — paired
           with .cal-cells' minmax(0, 1fr) tracks above so a long class-bar
           title clips inside its own cell instead of stretching the column. */
      min-width: 0;
      display: flex;
      flex-direction: column;
      padding: 0.3rem 0.35rem;
      background: #fff;
      cursor: pointer;
      font-size: 0.75rem;
      border-right: 1px solid var(--zui-line);
      border-bottom: 1px solid var(--zui-line);
    }
    .cal-cell:nth-child(7n) {
      border-right: 0;
    }
    .cal-cell:nth-last-child(-n + 7) {
      border-bottom: 0;
    }
    .cal-cell.outside {
      background: var(--zui-panel);
    }
    .cal-cell.outside .cal-daynum {
      color: var(--zui-details);
      opacity: 0.55;
    }
    .cal-daynum {
      font-size: 0.78rem;
      font-weight: 600;
      color: var(--zui-headline);
      align-self: flex-start;
    }
    .cal-daynum.today {
      /* N4: the day number was riding high/left inside its accent circle
           (grid baseline + inherited align-self left the glyph off-center).
           Flex-center it with a reset line-height and equal min box so the
           number sits dead-center; horizontal padding only ever grows a
           two-digit date, never breaking the round shape. NB: the circle stays
           round in every shape (Square only flattens the calendar chrome). */
      display: inline-flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
      min-width: 1.5rem;
      height: 1.5rem;
      padding: 0 0.25rem;
      line-height: 1;
      border-radius: 999px;
      background: var(--zui-accent);
      color: var(--zui-btn-text, #fff);
    }
    .cal-bars {
      display: flex;
      flex-direction: column;
      gap: 2px;
      margin-top: 0.25rem;
    }
    .cal-bar {
      appearance: none;
      border: 0;
      display: block;
      width: 100%;
      text-align: left;
      padding: 2px 6px;
      /* N4: Square shape flattens each class bar (0px); other shapes keep 4px. */
      border-radius: var(--zui-square-radius, 4px);
      background: var(--cal-bar-bg, var(--zui-accent));
      color: var(--cal-bar-ink, var(--zui-btn-text, #fff));
      font: inherit;
      font-size: 0.64rem;
      font-weight: 600;
      line-height: 1.45;
      cursor: pointer;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .cal-bar:hover {
      background: var(--cal-bar-bg, var(--zui-accent-deep, var(--zui-accent)));
    }
    .cal-bar-time {
      font-weight: 700;
      opacity: 0.85;
    }
    .cal-bar.cancelled {
      opacity: 0.55;
      text-decoration: line-through;
    }
    .cal-dots {
      display: none;
      gap: 3px;
      flex-wrap: wrap;
      margin-top: 0.2rem;
    }
    .cal-dot {
      width: 5px;
      height: 5px;
      border-radius: 999px;
      background: var(--cal-bar-bg, var(--zui-accent));
    }
    .cal-more {
      font-size: 0.58rem;
      font-weight: 600;
      color: var(--zui-details);
      padding-left: 3px;
    }
    /* Class detail popover — opened by clicking a bar (desktop). */
    .cal-pop-backdrop {
      position: fixed;
      inset: 0;
      z-index: 20;
    }
    .cal-popover {
      position: absolute;
      z-index: 21;
      width: 15rem;
      max-width: calc(100% - 1rem);
      background: #fff;
      border: 1px solid var(--zui-line);
      border-radius: 12px;
      box-shadow: 0 12px 32px -10px rgba(31, 42, 34, 0.4);
      padding: 0.8rem 0.9rem 0.9rem;
    }
    .cal-pop-close {
      position: absolute;
      top: 0.35rem;
      right: 0.45rem;
      appearance: none;
      border: 0;
      background: none;
      cursor: pointer;
      color: var(--zui-details);
      font-size: 1rem;
      line-height: 1;
      padding: 0.15rem;
    }
    .cal-pop-time {
      font-size: 0.64rem;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      color: var(--zui-details);
    }
    .cal-pop-name {
      font-family: var(--zui-font);
      font-weight: 700;
      font-size: 0.95rem;
      color: var(--zui-headline);
      margin: 0.15rem 0;
      padding-right: 1rem;
    }
    .cal-pop-meta {
      font-size: 0.78rem;
      color: var(--zui-details);
    }
    .cal-pop-cap {
      font-size: 0.75rem;
      font-weight: 600;
      margin-top: 0.4rem;
      color: var(--zui-headline);
    }
    .cal-pop-book {
      margin-top: 0.65rem;
      width: 100%;
      appearance: none;
      border: 0;
      border-radius: var(--zui-btn-radius, 999px);
      background: var(--zui-accent);
      color: var(--zui-btn-text, #fff);
      font: inherit;
      font-weight: 700;
      font-size: 0.8rem;
      padding: 0.5rem;
      cursor: pointer;
    }
    .cal-pop-book:hover {
      background: var(--zui-accent-deep, var(--zui-accent));
    }
    .cal-pop-cancel {
      margin-top: 0.55rem;
      font-size: 0.78rem;
      font-weight: 700;
      color: var(--zui-details);
    }
    /* Day-detail list: shown on both desktop + mobile when a day is clicked
         (auto-scrolled into view). On mobile it's the primary booking surface
         (dots don't carry class identity); on desktop it complements the bars. */
    .cal-detail {
      margin-top: 1.25rem;
    }
    @media (max-width: 640px) {
      .cal-cell {
        min-height: 46px;
      }
      .cal-bars {
        display: none;
      }
      .cal-dots {
        display: flex;
      }
      .cal-tb-spacer {
        display: none;
      }
      .cal-month-btn {
        font-size: 0.95rem;
      }
    }
    /* SCH: G2 — Week view (7 day-columns of class cards; folds in the old
         Calendar embed's week layout). */
    .wk {
      padding: 0.5rem 0 1rem;
    }
    .wk-nav {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      margin-bottom: 0.75rem;
    }
    .wk-range {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
    }
    .wk-label {
      font-family: var(--zui-font);
      font-weight: 700;
      font-size: 1rem;
      color: var(--zui-headline);
      min-width: 9.5rem;
      text-align: center;
    }
    .wk-grid {
      display: grid;
      /* SC8: condense all 7 columns to fit the embed width on desktop, like
           the calendar grid (repeat(7, 1fr)). minmax(0, 1fr) lets each column
           shrink below its content width so the 7th day is never clipped. The
           narrow-screen fallback below restores a real per-column min width and
           lets the grid scroll horizontally when it gets too tight to read. */
      grid-template-columns: repeat(7, minmax(0, 1fr));
      gap: 6px;
      overflow-x: auto;
    }
    .wk-col {
      border: 1px solid var(--zui-line);
      border-radius: var(--zui-square-radius, 10px);
      background: var(--zui-panel);
      padding: 0.4rem 0.4rem 0.6rem;
      min-height: 8rem;
      /* SC8: allow the column to shrink below its content width so 7
           minmax(0, 1fr) tracks fit the embed instead of overflowing. */
      min-width: 0;
    }
    .wk-colhead {
      text-align: center;
      padding: 0.25rem 0 0.5rem;
      border-bottom: 1px solid var(--zui-line);
      margin-bottom: 0.5rem;
    }
    .wk-colhead .wk-dow {
      display: block;
      font-size: 0.6rem;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: var(--zui-details);
      /* SC8: tighten the leading between the day-of-week and the date. */
      line-height: 1.1;
    }
    .wk-colhead .wk-date {
      display: block;
      font-family: var(--zui-font);
      font-weight: 700;
      font-size: 1.05rem;
      color: var(--zui-headline);
      line-height: 1.15;
    }
    .wk-colhead.today .wk-date {
      color: var(--zui-accent-deep, var(--zui-accent));
    }
    .wk-cards {
      display: flex;
      flex-direction: column;
      gap: 0.4rem;
    }
    .wk-card {
      border: 1px solid var(--zui-line);
      border-radius: var(--zui-square-radius, 8px);
      background: #fff;
      padding: 0.45rem 0.5rem;
    }
    .wk-card.cancelled {
      opacity: 0.6;
    }
    /* Zone label beside a week-card time — same abbreviation the list rows
         and the events embed use, dimmed so it reads as a qualifier. */
    .wk-tz {
      margin-left: 0.25rem;
      opacity: 0.75;
    }
    .wk-time {
      font-size: 0.62rem;
      font-weight: 700;
      color: var(--zui-details);
      text-transform: uppercase;
      letter-spacing: 0.03em;
      /* SC8: tighten the leading between time / title / instructor / location
           so the week card reads compactly instead of double-spaced. */
      line-height: 1.2;
    }
    .wk-name {
      font-family: var(--zui-font);
      font-weight: 700;
      font-size: 0.82rem;
      color: var(--zui-headline);
      margin: 0.05rem 0;
      line-height: 1.2;
    }
    .wk-meta {
      font-size: 0.68rem;
      color: var(--zui-details);
      line-height: 1.25;
    }
    .wk-cap {
      margin-top: 0.3rem;
      font-size: 0.68rem;
      font-weight: 700;
      color: var(--zui-headline);
    }
    .wk-cap.waiting {
      font-weight: 500;
      color: var(--zui-panel-ink-soft);
    }
    .wk-book {
      margin-top: 0.4rem;
      width: 100%;
      appearance: none;
      border: 0;
      border-radius: var(--zui-btn-radius, 999px);
      background: var(--zui-accent);
      color: var(--zui-btn-text, #fff);
      font: inherit;
      font-size: 0.72rem;
      font-weight: 700;
      padding: 0.4rem 0.5rem;
      cursor: pointer;
    }
    .wk-book:hover {
      background: var(--zui-accent-deep, var(--zui-accent));
    }
    .wk-book.waitlist {
      background: var(--zui-panel);
      color: var(--zui-accent-deep, var(--zui-accent));
      border: 1.5px solid var(--zui-accent);
    }
    .wk-cancel {
      margin-top: 0.4rem;
      font-size: 0.68rem;
      font-weight: 700;
      color: var(--zui-details);
    }
    .wk-empty {
      font-size: 0.7rem;
      color: var(--zui-details);
      text-align: center;
      padding: 0.5rem 0;
    }

    /* SC8: below the point where 7 condensed columns get too cramped to read,
         give each column a real min width and let the grid scroll horizontally
         instead. Placed after the base .wk-grid so it wins the cascade. */
    @media (max-width: 640px) {
      .wk-grid {
        grid-template-columns: repeat(7, minmax(8.5rem, 1fr));
      }
    }

    /* Narrow viewports (e.g. phones): compress the 7-day strip and class
         rows so they fit without wrapping/clipping. Kept last in this
         stylesheet so it wins the cascade over the desktop rules above at
         equal selector specificity. */
    @media (max-width: 480px) {
      .wrap {
        padding: 1.25rem 0.75rem 2rem;
      }
      /* MOB-26: swap the row of filter pills for a single "Filter" trigger,
           and the view segments for a native dropdown. */
      .filters {
        display: none;
      }
      .filter-trigger {
        display: inline-flex;
      }
      .switcher {
        display: none;
      }
      .view-select {
        display: block;
      }
      .switch-wrap::after {
        content: "";
        position: absolute;
        right: 0.8rem;
        top: 50%;
        width: 0.35rem;
        height: 0.35rem;
        border-right: 1.5px solid var(--zui-panel-ink-soft);
        border-bottom: 1.5px solid var(--zui-panel-ink-soft);
        transform: translateY(-75%) rotate(45deg);
        pointer-events: none;
      }
      /* MOB-27: tighten the gap between day sections and the day headings so
           the list isn't sparse on a phone. SCH-11: bump the top margin a
           touch so the first day heading doesn't crowd the sticky strip's
           drop-shadow now that the (shorter) Book buttons free up vertical
           rhythm in the rows below. */
      .days-list {
        margin-top: 1.375rem;
        gap: 1.25rem;
      }
      .day-head {
        font-size: 17px;
        line-height: 1.25;
      }
      .strip {
        padding: 0.625rem 0.25rem;
        gap: 2px;
      }
      .arrow {
        width: 24px;
        height: 24px;
      }
      .days {
        gap: 3px;
      }
      .day .cell {
        min-width: 0;
        padding: 6px 3px;
      }
      .day .num {
        font-size: 14px;
      }
      .day .dow {
        font-size: 9px;
        letter-spacing: 0.02em;
      }
      .day .dow-full {
        display: none;
      }
      .day .dow-short {
        display: inline;
        font-size: 10px;
        letter-spacing: 0;
      }
      /* SCH-11: the "has classes" dot fires for nearly every date in a
           typical week, so it reads as a stray, meaningless mark under each
           column rather than useful signal. Keep only the accent "today"
           dot, which is actually informative. */
      .day .dot:not(.today) {
        display: none;
      }
      .month-label {
        font-size: 14px;
      }
      .today-pill {
        font-size: 10px;
        padding: 3px 8px;
      }
      /* The sticky block is filters + month row + 7-day strip stacked. On a
           phone that's the single "Filter" trigger (the pill row is display:none
           at this breakpoint) plus two compact rows, so squeeze its padding to
           keep the block from eating the list it's supposed to navigate. */
      .sticky-head {
        padding-top: 0.25rem;
      }
      .sticky-head .controls {
        margin-bottom: 0.4rem;
      }
      /* Class rows: fix the trailing capacity/Book column to a narrow,
           predictable width instead of auto-sizing to the button's desktop
           padding — that's what was squeezing the title down to 2-3 lines. */
      /* Top of each card: time · thumb · name/meta. Capacity + Book move to
           their own full-width row below (see .right), so the title gets the
           whole width and nothing is crammed into a narrow trailing column. */
      .row {
        grid-template-columns: auto 1fr;
        gap: 0.1rem 0.6rem;
        row-gap: 0.3rem;
        /* Top-align the time / thumbnail with the first line of the title
             rather than centering them against the taller name+meta block. */
        align-items: start;
      }
      .row:not(.no-thumb) {
        grid-template-columns: auto 28px 1fr;
      }
      .row.no-thumb {
        grid-template-columns: auto 1fr;
      }
      .row.lines {
        padding: 0.7rem 2px;
      }
      .row.cards {
        padding: 0.7rem 1rem;
      }
      .row > div {
        min-width: 0;
      }
      .time {
        font-size: 16px;
        white-space: nowrap;
      }
      .dur {
        font-size: 11px;
      }
      .cname {
        font-size: 15px;
        line-height: 1.3;
        /* Flex row: the caret stays attached to the title's first line even
             when the title wraps to two lines. No reserved min-height — a
             one-line title no longer opens a big empty gap before the meta
             (that reserved height was the main source of the "loose" look). */
        display: flex;
        align-items: flex-start;
        gap: 0.3rem;
      }
      .cname > span:first-child {
        min-width: 0;
      }
      .cname .exp-icon {
        flex-shrink: 0;
        display: inline-flex;
        margin-top: 0.15em;
        margin-left: 0;
      }
      .cmeta {
        font-size: 11px;
        line-height: 1.4;
        margin-top: 3px;
      }
      /* Instructor · location stay on one inline line (with the dot
           separator) — the wider title column has room for them now, matching
           the target mobile design. Long combos wrap gracefully. */
      .cmeta {
        overflow-wrap: anywhere;
      }
      /* Location links share the exact teal used by the primary Book
           button/Sign in trigger, so on a narrow list they read as an
           equally "actionable" CTA rather than a secondary map link. Mute
           to the same neutral tone as the instructor text, with an
           always-on subtle underline so it still reads as tappable. */
      .cmeta a.loc {
        color: var(--zui-panel-ink-soft);
        text-decoration: underline;
        text-decoration-color: var(--zui-line);
        text-underline-offset: 2px;
      }
      .cmeta a.loc:hover,
      .cmeta a.loc:focus-visible {
        color: var(--zui-accent-deep);
        text-decoration-color: currentColor;
      }
      .thumb {
        width: 28px;
        height: 28px;
        /* Nudge the avatar toward the title beside it (rather than
             centered between time and title) so the two read as one
             visual group, instead of three evenly-stepped columns. */
        margin-left: 0.35rem;
      }
      /* Capacity + Book get their OWN full-width row under the title:
           the count sits left, the button right. With the whole row width to
           work with, the count never wraps and the button reads at a normal
           size — no more cramming into a 66px column. */
      .right {
        grid-column: 1 / -1;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        align-self: auto;
        gap: 0.5rem;
        margin-top: 2px;
      }
      .cap {
        text-align: left;
        font-size: 12px;
        white-space: nowrap;
      }
      .book {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: auto;
        padding: 0.45rem 1.15rem;
        font-size: 12.5px;
        line-height: 1;
        min-height: 34px;
        white-space: nowrap;
      }
      /* Full width row now has room for the full label. */
      .book-label-full {
        display: inline;
      }
      .book-label-short {
        display: none;
      }
    }
  `,
];
